import React from "react";
import { CompassRose, TrendUp, TrendDown, Minus, ArrowUp } from "@phosphor-icons/react";

const TREND_STYLE = {
  accelerating: { color: "#E63946", label: "Accelerating", Icon: TrendUp },
  slowing: { color: "#6BBF59", label: "Slowing", Icon: TrendDown },
  steady: { color: "#E9C46A", label: "Steady", Icon: Minus },
};

export const SpreadAnalysisCard = ({ spread }) => {
  if (!spread?.available) return null;
  const dir = spread.direction;
  const growth = spread.growth;
  if (!dir && !growth) return null;
  const trend = growth ? TREND_STYLE[growth.trend] || TREND_STYLE.steady : null;
  const hasSatData =
    Number.isFinite(spread.hotspot_count) &&
    spread.hotspot_count > 0 &&
    Number.isFinite(spread.window_hours);

  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4 mb-6" data-testid="spread-analysis-card">
      <div className="flex items-center gap-2 mb-3">
        <CompassRose size={14} weight="duotone" className="text-tac-info" />
        <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
          Progression &amp; Spread Direction
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div data-testid="spread-direction-block">
          <div className="text-[12px] uppercase tracking-wider font-bold text-tac-dim mb-1.5">
            Satellite Spread{hasSatData ? ` (${spread.hotspot_count} hotspots · ${Math.round(spread.window_hours)}h)` : ""}
          </div>
          {!hasSatData ? (
            <div className="text-sm text-tac-dim" data-testid="spread-no-hotspots-text">
              No recent satellite hotspots detected near this fire.
            </div>
          ) : dir?.moving ? (
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 shrink-0 rounded-full border border-tac-info/50 bg-tac-info/10 flex items-center justify-center"
                title={`Bearing ${dir.bearing_deg}°`}
              >
                <ArrowUp
                  size={22}
                  weight="bold"
                  className="text-tac-info"
                  style={{ transform: `rotate(${dir.bearing_deg}deg)` }}
                />
              </div>
              <div>
                <div className="text-lg font-black text-tac-text leading-tight" data-testid="spread-direction-text">
                  Spreading {dir.compass}
                </div>
                <div className="text-[13px] font-mono-tac tabular text-tac-dim">
                  ~{dir.distance_mi} mi cohort shift · ≈{dir.per_day_mi} mi/day
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-tac-text" data-testid="spread-holding-text">
              <span className="font-bold text-tac-safe">Holding</span> — no significant hotspot movement detected.
            </div>
          )}
        </div>
        {growth && (
          <div data-testid="spread-growth-block">
            <div className="text-[12px] uppercase tracking-wider font-bold text-tac-dim mb-1.5">
              Growth Rate ({growth.window_days}d of records)
            </div>
            <div className="flex items-center gap-3">
              {trend && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border"
                  style={{ color: trend.color, borderColor: `${trend.color}66`, background: `${trend.color}1a` }}
                  data-testid="spread-trend-chip"
                >
                  <trend.Icon size={11} weight="bold" /> {trend.label}
                </span>
              )}
              <div className="font-mono-tac tabular text-sm text-tac-text">
                {growth.rate_acres_per_day >= 0 ? "+" : ""}{growth.rate_acres_per_day} ac/day
                <span className="text-tac-faint"> · {growth.first_acres} → {growth.latest_acres} ac</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="text-[12px] font-mono-tac text-tac-faint mt-3 pt-2 border-t border-tac-border/60">
        <span className="font-bold text-tac-dim">How this works:</span> NASA/NOAA VIIRS satellites sweep
        overhead 4–6 times a day (roughly every 4–12 hours, day and night) and detect heat in ~375 m pixels.
        We compare where the older detections sit vs the newest ones — if the hot area's center has shifted,
        that's the direction the fire is moving. Hotspots on the map fade with age — brightest = newest.
        Estimates only; confirm with official sources.
      </div>
    </div>
  );
};

export default SpreadAnalysisCard;
